import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dropdown,
  Modal,
  Spec,
  Row,
  Col,
  formatPrice,
} from '@lucid/lego';
import type {
  OrderDetailsResponse,
  UpdateOrderRequest,
} from '../../../../../../api/services/lupp/order/dto.order';
import { useUpdateOrder } from '../../../../../../api/services/lupp/order/order';
import { useGetUserLocations } from '../../../../../../api/services/lupp/locations/locations';
import { useUserRole } from '../../../../../../hooks/useUserRole';
import { getMappedOrderStatus, isOrderStatusPastAtEuFpr } from '../../../orderStatusMapping';
import { isEmbeddedSubventionMethod } from '../../PaymentFinancing/constants';
import { formatDate, formatDateTime } from '../../../../../../utils/tools';
import { useStore } from '../../../../../../store/store';
import styles from './Summary.module.scss';

type SummaryProps = {
  orderData: OrderDetailsResponse;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  locale: string;
  onDownloadAgreement: () => void;
  isAgreementAvailable: boolean;
  isFinalInvoiceGenerated?: boolean;
  dataTestId?: string;
};

/** Shared Spec props for all summary fields */
const SPEC_PROPS = {
  variant: 'gray' as const,
  theme: 'light' as const,
  size: 'extraSmall' as const,
  type: 'sans' as const,
};

/** How long the success banner stays visible before auto-dismissing (errors stay). */
const STATUS_MESSAGE_DURATION_MS = 3000;

/**
 * Real Auth0 JWT role-claim values (see src/hooks/useSchedulerRole.ts for the proven
 * reference). Note these differ from the `@lucid/auth` USER_ROLES constants
 * ('Agency-Admin-R4' etc.), which are authz-service frontend-resource role codes,
 * not the literal strings present in the `roles` custom claim.
 */
const DELIVERY_LOCATION_EDITOR_ROLES = new Set([
  'LucidPartners-Agency-Admin',
  'LucidPartners-Agency-Manager',
]);

/**
 * Order Details Summary component
 * Displays key order information: dates, status, pricing, payment, and agreement download.
 *
 * @component
 * @param {SummaryProps} props - Component props
 * @returns {JSX.Element} Order details summary grid
 * @example
 * <Summary
 *   orderData={orderData}
 *   theme="dark"
 *   labels={labels}
 *   locale="en-US"
 *   onDownloadAgreement={handleDownload}
 * />
 */
const Summary: React.FC<SummaryProps> = ({
  orderData,
  theme,
  labels,
  locale,
  onDownloadAgreement,
  isAgreementAvailable,
  isFinalInvoiceGenerated = false,
  dataTestId = 'order-details-summary',
}) => {
  const language = useStore((state) => state.language) ?? undefined;
  const mappedStatus = getMappedOrderStatus(orderData.orderStatus);

  const { roles } = useUserRole();
  const canEditDeliveryLocation = useMemo(() => {
    const hasEditorRole = roles.some((role) => DELIVERY_LOCATION_EDITOR_ROLES.has(role));
    if (!hasEditorRole) return false;
    if (mappedStatus === 'delivered' || mappedStatus === 'cancelled') return false;
    if (isOrderStatusPastAtEuFpr(orderData.orderStatus)) return false;
    if (isFinalInvoiceGenerated) return false;
    return true;
  }, [roles, mappedStatus, orderData.orderStatus, isFinalInvoiceGenerated]);

  // Only the locations assigned to the signed-in user (org-wide admins get all of them).
  const { data: locationsData } = useGetUserLocations();
  const deliveryLocationOptions = useMemo(
    () =>
      (locationsData?.items ?? []).map((location) => ({
        value: location.locationIdentifier,
        label: location.city
          ? `${location.locationName ?? location.locationIdentifier} - ${location.city}, ${location.countryCode}`
          : (location.locationName ?? location.locationIdentifier),
      })),
    [locationsData?.items]
  );

  // Delivery location comes from the order's Partner Portal delivery location (not the
  // service-center preference), matched by identifier.
  const selectedDeliveryLocationValue = orderData.deliveryLocation?.locationIdentifier ?? '';

  const deliveryLocationTitle =
    orderData.deliveryLocation?.locationName ||
    deliveryLocationOptions.find((option) => option.value === selectedDeliveryLocationValue)
      ?.label ||
    '-';

  const { mutate: updateOrder, isPending: isSavingLocation } = useUpdateOrder();
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [selectedLocationValue, setSelectedLocationValue] = useState('');
  const [locationSaveStatus, setLocationSaveStatus] = useState<'success' | 'error' | null>(null);
  const locationStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending auto-dismiss timeout on unmount.
  useEffect(() => {
    return () => {
      if (locationStatusTimeoutRef.current) {
        clearTimeout(locationStatusTimeoutRef.current);
      }
    };
  }, []);

  const showLocationSaveStatus = (status: 'success' | 'error') => {
    if (locationStatusTimeoutRef.current) {
      clearTimeout(locationStatusTimeoutRef.current);
    }
    setLocationSaveStatus(status);
    // Errors stay until the next action, matching the prospect Change Location banner.
    if (status === 'success') {
      locationStatusTimeoutRef.current = setTimeout(
        () => setLocationSaveStatus(null),
        STATUS_MESSAGE_DURATION_MS
      );
    }
  };

  const handleOpenLocationModal = () => {
    setSelectedLocationValue(selectedDeliveryLocationValue);
    setLocationSaveStatus(null);
    setIsLocationModalOpen(true);
  };

  const handleCloseLocationModal = () => {
    setIsLocationModalOpen(false);
  };

  const handleConfirmLocationChange = () => {
    const option = deliveryLocationOptions.find(
      (location) => location.value === selectedLocationValue
    );
    if (!option) return;
    if (option.value === selectedDeliveryLocationValue) {
      setIsLocationModalOpen(false);
      return;
    }

    updateOrder(
      {
        orderIdentifier: orderData.orderIdentifier,
        orderDeliveryLocationIdentifier: option.value,
      } as UpdateOrderRequest,
      {
        onSuccess: () => {
          setIsLocationModalOpen(false);
          showLocationSaveStatus('success');
        },
        onError: () => {
          setIsLocationModalOpen(false);
          showLocationSaveStatus('error');
        },
      }
    );
  };

  const statusLabelMap: Record<string, string> = {
    'awaiting assignment':
      labels?.['const.ORDER_STATUS.awaitingAssignment'] ?? 'Awaiting assignment',
    'planning production':
      labels?.['const.ORDER_STATUS.planningProduction'] ?? 'Planning Production',
    'production planned': labels?.['const.ORDER_STATUS.productionPlanned'] ?? 'Production Planned',
    'in production': labels?.['const.ORDER_STATUS.inProduction'] ?? 'In Production',
    'factory gated': labels?.['const.ORDER_STATUS.factoryGated'] ?? 'Factory Gated',
    'in transit to eu fpr':
      labels?.['const.ORDER_STATUS.inTransitToEuFpr'] ?? 'In Transit to EU FPR',
    'at eu fpr': labels?.['const.ORDER_STATUS.atEuFpr'] ?? 'At EU FPR',
    dispatched: labels?.['const.ORDER_STATUS.dispatched'] ?? 'Dispatched',
    'on-site': labels?.['const.ORDER_STATUS.onSite'] ?? 'On-site',
    delivered: labels?.['const.ORDER_STATUS.delivered'] ?? 'Delivered',
    cancelled: labels?.['const.ORDER_STATUS.cancelled'] ?? 'Cancelled',
  };

  const orderStatusTitle = statusLabelMap[mappedStatus] ?? 'Awaiting assignment';

  return (
    <Box data-testid={dataTestId}>
      <Box mb={24}>
        <Typography variant="subtitle" size="small" theme={theme} color="black">
          {labels?.orderDetails || 'Order Details'}
        </Typography>
      </Box>
      {locationSaveStatus === 'success' && (
        <Box
          mb={16}
          pt={12}
          pb={12}
          pl={16}
          pr={16}
          className={styles.successMessageText}
          data-testid={`${dataTestId}-success-message`}
        >
          <Typography variant="body" size="medium" color="white" theme={theme}>
            {labels?.['form.locationUpdateSuccess'] ?? 'Location updated successfully'}
          </Typography>
        </Box>
      )}
      {locationSaveStatus === 'error' && (
        <Box
          mb={16}
          pt={12}
          pb={12}
          pl={16}
          pr={16}
          className={styles.errorMessageText}
          data-testid={`${dataTestId}-error-message`}
        >
          <Typography variant="body" size="medium" color="white" theme={theme}>
            {labels?.['form.locationUpdateError'] ?? 'Failed to update location. Please try again.'}
          </Typography>
        </Box>
      )}
      <Box pt={24} pl={24} pb={24} pr={24} background={'gray-1'}>
        <Row gutter={[24, 0]}>
          <Col sm={12}>
            <Row gutter={[24, 0]} justify="space-between">
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.orderDate || 'Order Date'}
                  title={formatDate(orderData.orderCreatedDate, locale, {}, language)}
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.orderStatus || 'Order Status'}
                  title={orderStatusTitle}
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.totalPrice || 'Total Price'}
                  title={
                    isEmbeddedSubventionMethod(orderData.subventionMethod)
                      ? '-'
                      : formatPrice(orderData.orderTotal, { locale })
                  }
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.preferredPaymentMethod || 'Preferred Payment Method'}
                  title={orderData.preferredPaymentMethod || '-'}
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.financingStatus || 'Financing Status'}
                  title={orderData.financingStatus || '-'}
                />
              </Col>
            </Row>
          </Col>

          <Col sm={12}>
            <Row gutter={[24, 24]} justify="space-between">
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.scheduledDeliveryDate || 'Scheduled Delivery Date'}
                  title={formatDateTime(orderData.scheduledDeliveryDate, locale, language)}
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.handoffDate || 'Handoff Date'}
                  title={formatDate(orderData.handOffDate, locale, {}, language)}
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.warrantyStartDate || 'Warranty Start Date'}
                  title={formatDate(orderData.warrantyStartDate, locale, {}, language)}
                />
              </Col>

              <Col sm={6} lg={2}>
                <Box className={styles.deliveryLocationRow}>
                  <Spec
                    {...SPEC_PROPS}
                    eyebrow={labels?.['form.deliveryLocation'] || 'Delivery Location'}
                    title={deliveryLocationTitle}
                  />
                  {canEditDeliveryLocation && (
                    <Button
                      type="tertiary"
                      color="sapphire"
                      size="small"
                      theme={theme === 'dark' ? 'light' : 'dark'}
                      className={styles.changeLocationButton}
                      dataTestId="change-delivery-location-button"
                      gaComponent="order-summary"
                      label={labels?.['form.changeLocation'] ?? 'Change Location'}
                      handleClick={handleOpenLocationModal}
                    />
                  )}
                </Box>
                {isLocationModalOpen && (
                  <Modal
                    dataTestId="change-delivery-location-modal"
                    isOpen={isLocationModalOpen}
                    onClose={handleCloseLocationModal}
                    title={labels?.['form.changeLocationTitle'] ?? 'Change Delivery Location'}
                    theme={theme === 'dark' ? 'light' : 'dark'}
                    footer={
                      <Box className={styles.modalFooter}>
                        <Button
                          type="secondary"
                          color="neutral"
                          size="small"
                          theme={theme === 'dark' ? 'light' : 'dark'}
                          dataTestId="change-delivery-location-cancel-button"
                          gaComponent="order-summary"
                          label={labels?.['common.cancelButton'] ?? 'Cancel'}
                          handleClick={handleCloseLocationModal}
                          disabled={isSavingLocation}
                        />
                        <Button
                          type="primary"
                          color="neutral"
                          size="small"
                          theme={theme === 'dark' ? 'light' : 'dark'}
                          dataTestId="change-delivery-location-confirm-button"
                          gaComponent="order-summary"
                          label={
                            isSavingLocation
                              ? (labels?.['form.saving'] ?? 'Saving...')
                              : (labels?.['form.confirmAssignment'] ?? 'Confirm Assignment')
                          }
                          handleClick={handleConfirmLocationChange}
                          disabled={isSavingLocation || !selectedLocationValue}
                        />
                      </Box>
                    }
                  >
                    <Dropdown
                      theme={theme === 'dark' ? 'light' : 'dark'}
                      label={labels?.['form.deliveryLocation'] ?? 'Delivery Location'}
                      name="deliveryLocation"
                      selectedOption={selectedLocationValue}
                      options={deliveryLocationOptions}
                      handleSelect={(value: string) => setSelectedLocationValue(value)}
                    />
                  </Modal>
                )}
              </Col>

              <Col sm={6} lg={2}>
                <Spec {...SPEC_PROPS} eyebrow={labels?.orderAgreement || 'Order Agreement'} />
                {isAgreementAvailable ? (
                  <IconButton
                    buttonType="tertiary"
                    color="neutral"
                    dataTestId="download-agreement-button"
                    gaComponent="IconButton"
                    handleClick={onDownloadAgreement}
                    icon={{ category: 'global', name: 'file' }}
                    size="small"
                    theme={theme === 'dark' ? 'light' : 'dark'}
                  />
                ) : (
                  <Spec {...SPEC_PROPS} title="-" />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Box>
    </Box>
  );
};

Summary.displayName = 'Summary';

export default Summary;
